//
// # Render Stats
//
// Per-frame rendering statistics, populated by `DrawToHtmlCanvas` (and other
// draw-to backends) and consumed by the `CountersDisplay` panel.
//
// Organized into three sections:
// - `instances`  — counts from `DrawTo.instance()` (sprite/vector/culled/sizeCulled)
// - `overlays`   — counts of overlay draws (routes, labels, highlights, outlines)
// - `arrays`     — counts of array instances (total / clipped / fullyCulled)
//
// Instance-level stats are bumped inside `DrawTo.instance()`.
// Overlay `drawn` counts are bumped by the primitive methods (`bbox`, `path`,
// `text`, etc.) when inside a `DrawTo.overlay(kind, fn)` scope.
// Overlay `culled` / `sizeCulled` counts are bumped by the callers that do
// their own culling before reaching the draw primitives (e.g. `route.ts`,
// `layout.redrawPathElements`).
// Array stats are bumped by `InstanceEntity.redraw()`'s array fast path.
//

/** The kinds of overlay we track counts for. */
export type OverlayKind = "routes" | "labels";

/** Counts for a single overlay bucket. */
export interface OverlayBucket {
  drawn: number;
  culled: number;
  sizeCulled: number;
}

/** Counts for array-instance redraw. */
export interface ArrayCounts {
  total: number; // Total array instances encountered
  clipped: number; // Arrays partially clipped by the viewport (visible < total segments)
  fullyCulled: number; // Arrays where the entire array was off-screen
}

/** Per-source segment sub-totals (see `SegmentCounts`). */
export interface SegmentSourceCounts {
  total: number;
  drawn: number;
  offScreen: number;
}

/**
 * Counts for individual segments within segmented and array instances.
 *
 * Aggregate invariant: `total === drawn + offScreen`.
 * Also: `total === array.total + freeform.total` (and likewise for drawn
 * and offScreen) — the aggregates are sums of the per-source breakdowns.
 *
 * **Fields:**
 * - `total` / `drawn` / `offScreen` — aggregates across both sources.
 * - `array` — segments that belong to array instances (regular 2D grids
 *             via `ArrayXform`). Their `offScreen` can come from either
 *             the array-grid fast path (grid-math cull before iteration)
 *             or from `Segment.redraw`'s own viewport self-cull.
 * - `freeform` — segments that belong to non-array segmented instances
 *                (manually-specified segment lists). Their `offScreen`
 *                comes only from `Segment.redraw`'s self-cull, since the
 *                array fast path does not apply.
 *
 * The per-source split is intended for reading the counters panel: given
 * a big `total`, a reader can see at a glance whether the work is coming
 * from a few large arrays or from many freeform segmented instances.
 */
export interface SegmentCounts {
  total: number;
  drawn: number;
  offScreen: number;
  array: SegmentSourceCounts;
  freeform: SegmentSourceCounts;
}

/** Counts for instance-level rendering (`DrawTo.instance()`). */
export interface InstanceCounts {
  culled: number;
  sizeCulled: number;
  // Sprite-path instances, split by whether the sprite was reused from the
  // cache (`spritesCached`: cheap `drawImage`) or freshly built this frame
  // (`spritesBuilt`: full rasterization). The total sprite count is the sum.
  spritesCached: number;
  spritesBuilt: number;
  vector: number;
}

/** The rendering context a primitive was drawn in. See `ElementCounts`. */
export type ElementBucket = "sprite" | "vector" | "overlay" | "direct";

/**
 * Per-bucket breakdown of `elementsDrawn`. Every primitive draw is counted
 * against exactly one bucket, and the four buckets sum to `elementsDrawn`.
 *
 * - `sprite`  — primitives emitted during sprite rasterization (cache-miss
 *               build work; an expensive frame when the sprite cache is cold).
 * - `vector`  — primitives emitted inside a vector-rendered instance (an
 *               instance too big to sprite).
 * - `overlay` — primitives emitted inside a `DrawTo.overlay(...)` scope
 *               (routes, labels, etc., drawn outside the sprite cache).
 * - `direct`  — everything else: root-level draws not inside an instance or
 *               overlay (top-level outline, UI widgets).
 */
export interface ElementCounts {
  sprite: number;
  vector: number;
  overlay: number;
  direct: number;
  // Elements skipped by per-element culls *inside* `LayoutEntity.redrawElements`
  // (i.e. the cell's own non-path geometry at root / inside vector). These are
  // NOT part of the sprite/vector/overlay/direct sum — they represent work
  // avoided. Populated via `DrawTo.elementCulled()` / `elementSizeCulled()`.
  culled: number;
  sizeCulled: number;
}

/** The shared stats container, reset once per frame by the draw-to backend. */
export class RenderStats {
  readonly instances: InstanceCounts = {
    culled: 0,
    sizeCulled: 0,
    spritesCached: 0,
    spritesBuilt: 0,
    vector: 0,
  };

  readonly elements: ElementCounts = {
    sprite: 0,
    vector: 0,
    overlay: 0,
    direct: 0,
    culled: 0,
    sizeCulled: 0,
  };

  readonly overlays: Record<OverlayKind, OverlayBucket> = {
    routes: { drawn: 0, culled: 0, sizeCulled: 0 },
    labels: { drawn: 0, culled: 0, sizeCulled: 0 },
  };

  readonly arrays: ArrayCounts = {
    total: 0,
    clipped: 0,
    fullyCulled: 0,
  };

  readonly segments: SegmentCounts = {
    total: 0,
    drawn: 0,
    offScreen: 0,
    array: { total: 0, drawn: 0, offScreen: 0 },
    freeform: { total: 0, drawn: 0, offScreen: 0 },
  };

  // Total primitive draw calls. Includes both in-instance geometry (inside
  // sprites) and overlay draws. `elementsDrawnMax` is the high-water mark
  // across all frames since the last `invalidate()`.
  elementsDrawn: number = 0;
  elementsDrawnMax: number = 0;

  /** Zero per-frame counters. Preserves `elementsDrawnMax`. */
  reset(): void {
    this.instances.culled = 0;
    this.instances.sizeCulled = 0;
    this.instances.spritesCached = 0;
    this.instances.spritesBuilt = 0;
    this.instances.vector = 0;

    this.elements.sprite = 0;
    this.elements.vector = 0;
    this.elements.overlay = 0;
    this.elements.direct = 0;
    this.elements.culled = 0;
    this.elements.sizeCulled = 0;

    const overlayKinds: OverlayKind[] = ["routes", "labels"];
    for (const k of overlayKinds) {
      const bucket = this.overlays[k];
      bucket.drawn = 0;
      bucket.culled = 0;
      bucket.sizeCulled = 0;
    }

    this.arrays.total = 0;
    this.arrays.clipped = 0;
    this.arrays.fullyCulled = 0;

    this.segments.total = 0;
    this.segments.drawn = 0;
    this.segments.offScreen = 0;
    this.segments.array.total = 0;
    this.segments.array.drawn = 0;
    this.segments.array.offScreen = 0;
    this.segments.freeform.total = 0;
    this.segments.freeform.drawn = 0;
    this.segments.freeform.offScreen = 0;

    this.elementsDrawnMax = Math.max(this.elementsDrawnMax, this.elementsDrawn);
    this.elementsDrawn = 0;
  }
}
